import { getID } from 'utils/id';
import { collectFingerprint } from './collect';

function extractStableSignals(fp: Awaited<ReturnType<typeof collectFingerprint>>) {
  return {
    // Screen — hardware-bound display properties only
    s_w: fp.screen.width,
    s_h: fp.screen.height,
    s_cd: fp.screen.colorDepth,
    s_pr: fp.screen.pixelRatio,

    // Hardware — all properties are hardware-bound
    h_c: fp.hardware.cores,
    h_m: fp.hardware.memory,
    h_tp: fp.hardware.maxTouchPoints,
    h_p: fp.hardware.platform,

    // Browser — config-level properties only (no UA, no doNotTrack, no webdriver)
    b_l: fp.browser.language,
    b_ls: fp.browser.languages,
    b_pv: fp.browser.pdfViewerEnabled,

    // Media codecs — determined by browser+OS+hardware
    m_vc: fp.media.videoCodecs,
    m_ac: fp.media.audioCodecs,

    // Storage — browser privacy configuration
    st_ls: fp.storage.localStorageAvailable,
    st_ss: fp.storage.sessionStorageAvailable,
    st_idb: fp.storage.indexedDBAvailable,

    // Math — deterministic per JS engine
    ma_t: fp.math.tan,
    ma_a2: fp.math.atan2,
    ma_ac: fp.math.acos,
    ma_sh: fp.math.sinh,
  };
}

function deterministicSerialize(fingerprint: Awaited<ReturnType<typeof collectFingerprint>>) {
  const signals = extractStableSignals(fingerprint);

  return JSON.stringify(signals, (_key, value) => {
    if (value === undefined || (typeof value === 'number' && isNaN(value))) {
      return null;
    }

    return value;
  });
}

/**
 * SHA-256 via Web Crypto API. Available in all modern browsers (Chrome 37+,
 * Firefox 34+, Safari 7+). Returns a hex string.
 */
async function sha256(input: string): Promise<string> {
  const encoded = new TextEncoder().encode(input);
  const buffer = await crypto.subtle.digest('SHA-256', encoded);
  const bytes = new Uint8Array(buffer);

  let hex = '';

  for (let i = 0; i < bytes.length; i++) {
    hex += bytes[i].toString(16).padStart(2, '0');
  }

  return hex;
}

/**
 * Collects all fingerprint signals then generates a deterministic hash from
 * the stable subset. Returns the FPJS deviceId with our in-house hash
 * appended, separated by a dot.
 *
 * Format: `{fpjs_visitorId}.{inhouse_sha256_hex}`
 *
 * If FPJS failed (deviceId is empty), the format is just the hash:
 * `_.{inhouse_sha256_hex}`
 */
export async function getFingerprint() {
  const fingerprint = await collectFingerprint();

  const serialized = deterministicSerialize(fingerprint);

  const hash = await sha256(serialized);

  const suffix = fingerprint.deviceId || getID();

  return {
    /** Combined ID: FPJS deviceId + in-house hash */
    id: `${hash}.${suffix}`,

    deviceId: fingerprint.deviceId,

    qualifier: hash,

    raw: fingerprint,
  };
}
